import React, { useEffect, useState } from "react";
import { arrivalNotificationAPI } from "../../utils/api";
import AppLayout from "../../components/ui/AppLayout";
import HeaderBar from "../../components/ui/HeaderBar";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import Icon from "../../components/AppIcon";
import Notifier from "../../components/ui/Notifier";
import UpdateRemarksModal from "./UpdateRemarksModal";
import { useAuth } from "../../context/AuthContext";
import { useNotifier } from '../../context/NotificationContext';
import { useConfirm } from '../../context/ConfirmContext';

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-800",
  sent: "bg-blue-100 text-blue-800",
  resend: "bg-purple-100 text-purple-800",
  acknowledged: "bg-green-100 text-green-800",
};

const STATUS_ICONS = {
  draft: "FileText",
  sent: "Send",
  resend: "RefreshCw",
  acknowledged: "CheckCircle",
};

export default function ArrivalNotificationsPage() {
  const notify = useNotifier();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [stats, setStats] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const [filters, setFilters] = useState({
    entryDateFrom: "",
    entryDateTo: "",
    status: "",
  });
  const [expandedDates, setExpandedDates] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [remarksModalNotification, setRemarksModalNotification] = useState(null); // For Update Remarks modal

  useEffect(() => {
    fetchSummary();
  }, [filters]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.entryDateFrom) params.entry_date_from = filters.entryDateFrom;
      if (filters.entryDateTo) params.entry_date_to = filters.entryDateTo;
      if (filters.status) params.status = filters.status;

      const result = await arrivalNotificationAPI.getSummary(params);
      setSummaryData(result.data || []);
      setStats(result.stats || null);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
      setNotification({
        show: true,
        type: "error",
        message: "Failed to load arrival notifications",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (notificationId, employerEmail) => {
    // Prompt for email address (pre-filled with existing email)
    const emailToSend = prompt(
      "Confirm recipient email address for arrival notification:",
      employerEmail || ""
    );

    if (emailToSend === null) return; // User cancelled
    if (!emailToSend.trim()) {
      notify.warning("Email address is required.");
      return;
    }

    setSendingId(notificationId);
    try {
      // Pass the displayed email to ensure it's sent to the correct recipient
      const result = await arrivalNotificationAPI.send(notificationId, emailToSend.trim());
      setNotification({
        show: true,
        type: "success",
        message: result.message || "Notification sent successfully",
      });
      fetchSummary();
    } catch (error) {
      console.error("Send notification error:", error);
      setNotification({
        show: true,
        type: "error",
        message: error.response?.data?.error || "Failed to send notification",
      });
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    const confirmed = await confirm({
      title: "Delete this draft?",
      message: "The draft notification will be removed.",
      confirmLabel: "Delete draft",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await arrivalNotificationAPI.delete(notificationId);
      setNotification({
        show: true,
        type: "success",
        message: "Notification deleted successfully",
      });
      fetchSummary();
    } catch (error) {
      console.error("Delete notification error:", error);
      setNotification({
        show: true,
        type: "error",
        message: error.response?.data?.error || "Failed to delete notification",
      });
    }
  };

  const toggleDateExpanded = (date) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const clearFilters = () => {
    setFilters({
      entryDateFrom: "",
      entryDateTo: "",
      status: "",
    });
  };

  // Handle Update Remarks modal success
  const handleRemarksModalSuccess = (message) => {
    setRemarksModalNotification(null);
    fetchSummary();
    setNotification({
      show: true,
      type: "success",
      message: message || "Remarks updated successfully",
    });
  };

  return (
    <AppLayout
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
    >
      <HeaderBar
        userRole={user?.role || "clab_user"}
        userName={user?.full_name || user?.fullName || user?.username}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      <div className="space-y-6 max-w-full px-6 pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Icon name="Mail" size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">
                    Arrival Notifications
                  </h1>
                  <p className="text-teal-100">
                    Manage and track worker arrival notifications
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <BreadcrumbNavigation
          items={[
            { label: "Dashboard", href: "/clab-dashboard" },
            { label: "Arrival Notifications", href: "/clab-arrival-notifications" },
          ]}
        />

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Icon name="Calendar" size={24} className="text-gray-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.totalDates}
                  </div>
                  <div className="text-sm text-gray-500">Entry Dates</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Icon name="FileText" size={24} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.totalNotifications}
                  </div>
                  <div className="text-sm text-gray-500">Total Notifications</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Icon name="Users" size={24} className="text-indigo-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.totalWorkers}
                  </div>
                  <div className="text-sm text-gray-500">Total Workers</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.byStatus?.acknowledged || 0}
                  </div>
                  <div className="text-sm text-gray-500">Acknowledged</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entry Date From
              </label>
              <input
                type="date"
                value={filters.entryDateFrom}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, entryDateFrom: e.target.value }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entry Date To
              </label>
              <input
                type="date"
                value={filters.entryDateTo}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, entryDateTo: e.target.value }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="resend">Resend</option>
                <option value="acknowledged">Acknowledged</option>
              </select>
            </div>
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={fetchSummary}
              className="flex items-center space-x-2 px-4 py-2 bg-[#2D9B9B] text-white rounded-lg hover:bg-[#1A7A7A] transition-colors"
            >
              <Icon name="RefreshCw" size={18} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary by Date */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Icon
                name="Loader"
                size={40}
                className="animate-spin text-[#2D9B9B] mx-auto mb-4"
              />
              <p className="text-gray-500">Loading notifications...</p>
            </div>
          ) : summaryData.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Icon name="Inbox" size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No arrival notifications found</p>
              <p className="text-gray-400 text-sm mt-2">
                Create notifications from the application details page
              </p>
            </div>
          ) : (
            summaryData.map((dateGroup) => (
              <div
                key={dateGroup.entryDate}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Date Header */}
                <button
                  onClick={() => toggleDateExpanded(dateGroup.entryDate)}
                  className="w-full px-6 py-4 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Icon name="Calendar" size={20} className="text-[#2D9B9B]" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {dateGroup.formattedDate}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{dateGroup.totalNotifications} notification(s)</span>
                        <span>{dateGroup.totalWorkers} worker(s)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {dateGroup.statusBreakdown.draft > 0 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {dateGroup.statusBreakdown.draft} Draft
                        </span>
                      )}
                      {dateGroup.statusBreakdown.sent > 0 && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {dateGroup.statusBreakdown.sent} Sent
                        </span>
                      )}
                      {dateGroup.statusBreakdown.acknowledged > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          {dateGroup.statusBreakdown.acknowledged} Acknowledged
                        </span>
                      )}
                    </div>
                    <Icon
                      name={expandedDates[dateGroup.entryDate] ? "ChevronUp" : "ChevronDown"}
                      size={20}
                      className="text-gray-400"
                    />
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedDates[dateGroup.entryDate] && (
                  <div className="border-t border-gray-200">
                    {dateGroup.notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className="px-6 py-4 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="font-semibold text-gray-900">
                                {notif.companyName}
                              </span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  STATUS_COLORS[notif.status]
                                }`}
                              >
                                <Icon
                                  name={STATUS_ICONS[notif.status]}
                                  size={12}
                                  className="mr-1"
                                />
                                {notif.status.charAt(0).toUpperCase() +
                                  notif.status.slice(1)}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Quota Ref:</span>{" "}
                                <span className="text-gray-900">{notif.quotaReferenceNo}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">VDR:</span>{" "}
                                <span className="text-gray-900">{notif.vdrId}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Workers:</span>{" "}
                                <span className="font-medium text-gray-900">
                                  {notif.workerCount}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Email:</span>{" "}
                                <span className="text-gray-900">
                                  {notif.employerEmail || "-"}
                                </span>
                              </div>
                            </div>
                            {notif.status === "acknowledged" && notif.acknowledgedAt && (
                              <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                                <span className="text-green-700">
                                  Acknowledged by {notif.acknowledgedByName} on{" "}
                                  {notif.acknowledgedAt}
                                </span>
                              </div>
                            )}
                            {notif.status === "sent" && notif.emailSentAt && (
                              <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                                <span className="text-blue-700">
                                  Sent on {notif.emailSentAt}
                                </span>
                              </div>
                            )}
                            {notif.status === "resend" && notif.emailSentAt && (
                              <div className="mt-2 p-2 bg-purple-50 rounded text-sm">
                                <span className="text-purple-700">
                                  Resent on {notif.emailSentAt}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {notif.status === "draft" && (
                              <>
                                <button
                                  onClick={() => handleSendNotification(notif.id, notif.employerEmail)}
                                  disabled={sendingId === notif.id}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-[#2D9B9B] text-white text-sm rounded-lg hover:bg-[#1A7A7A] disabled:opacity-50 transition-colors"
                                >
                                  {sendingId === notif.id ? (
                                    <>
                                      <Icon
                                        name="Loader"
                                        size={14}
                                        className="animate-spin"
                                      />
                                      <span>Sending...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="Send" size={14} />
                                      <span>Send</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteNotification(notif.id)}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200 transition-colors"
                                >
                                  <Icon name="Trash2" size={14} />
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                            {(notif.status === "sent" || notif.status === "resend" || notif.status === "acknowledged") && (
                              <>
                                <button
                                  onClick={() => handleSendNotification(notif.id, notif.employerEmail)}
                                  disabled={sendingId === notif.id}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-teal-100 text-[#2D9B9B] text-sm rounded-lg hover:bg-teal-200 disabled:opacity-50 transition-colors"
                                >
                                  {sendingId === notif.id ? (
                                    <>
                                      <Icon
                                        name="Loader"
                                        size={14}
                                        className="animate-spin"
                                      />
                                      <span>Resending...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="RefreshCw" size={14} />
                                      <span>Resend</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => setRemarksModalNotification(notif)}
                                  className="flex items-center space-x-1 px-3 py-1.5 bg-amber-100 text-amber-700 text-sm rounded-lg hover:bg-amber-200 transition-colors"
                                >
                                  <Icon name="Edit3" size={14} />
                                  <span>Update Remarks</span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Workers list (collapsible) */}
                        <details className="mt-3">
                          <summary className="cursor-pointer text-sm text-[#2D9B9B] hover:text-[#1A7A7A]">
                            View {notif.workerCount} worker(s)
                          </summary>
                          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    No
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Worker ID
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Name
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Passport
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Nationality
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Entry Date
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    PIC
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                    Remarks
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {notif.workers.map((worker) => (
                                  <tr key={worker.workerId} className="hover:bg-gray-50">
                                    <td className="px-3 py-2">{worker.no}</td>
                                    <td className="px-3 py-2">{worker.workerId}</td>
                                    <td className="px-3 py-2 font-medium">
                                      {worker.fullName}
                                    </td>
                                    <td className="px-3 py-2">{worker.passportNo}</td>
                                    <td className="px-3 py-2">{worker.nationality}</td>
                                    <td className="px-3 py-2">{worker.entryDate || "-"}</td>
                                    <td className="px-3 py-2">{notif.sentByName || "-"}</td>
                                    <td className="px-3 py-2">
                                      {worker.remarks ? (
                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                                          {worker.remarks}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Update Remarks Modal */}
        <UpdateRemarksModal
          isOpen={!!remarksModalNotification}
          onClose={() => setRemarksModalNotification(null)}
          notification={remarksModalNotification}
          onSuccess={handleRemarksModalSuccess}
        />

        {/* Notifier */}
        <Notifier
          show={notification.show}
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ show: false, type: "info", message: "" })}
        />
      </div>
    </AppLayout>
  );
}
